import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faEnvelope, faLock } from '@fortawesome/free-solid-svg-icons';
import { loginUser, googleAuth } from '../services/api';
import { APP_ROUTES } from '../config/api.config';
import GoogleAuthButton from '../components/GoogleAuthButton';
import theme from '../styles/theme';

const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background: linear-gradient(145deg, ${theme.colors.sidebarFrom} 0%, ${theme.colors.sidebarTo} 45%, #243044 100%);
  position: fixed;
  inset: 0;
  padding: 20px;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 50% at 20% 20%, rgba(255, 107, 107, 0.2) 0%, transparent 50%),
      radial-gradient(ellipse 50% 40% at 80% 80%, rgba(255, 142, 83, 0.14) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const LoginCard = styled.div`
  background: ${theme.colors.card};
  padding: 36px 32px;
  border-radius: ${theme.radius.xl};
  box-shadow: ${theme.shadows.xl}, 0 0 0 1px rgba(255, 255, 255, 0.08);
  width: 100%;
  max-width: 420px;
  margin: auto;
  position: relative;
  z-index: 1;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base};
  animation: slideUp 0.45s cubic-bezier(0.34, 1.2, 0.64, 1);

  @media (min-width: 1024px) {
    max-width: 460px;
    padding: 44px 40px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl}, ${theme.shadows.glowSoft};
  }
`;

const Title = styled.h1`
  font-size: ${theme.typography.h1};
  font-weight: 800;
  margin-bottom: 8px;
  text-align: center;
  background: ${theme.colors.accentGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  letter-spacing: -0.04em;

  @media (min-width: 1024px) {
    font-size: 2rem;
    margin-bottom: 12px;
  }
`;

const HomeLink = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  padding: 10px 18px;
  border-radius: ${theme.radius.pill};
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(12px);
  transition: all ${theme.transitions.base};
  font-size: ${theme.typography.body};
  z-index: 2;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-2px);
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${theme.colors.textMuted};
  margin-bottom: 20px;
  font-size: ${theme.typography.body};
  line-height: 1.5;

  @media (min-width: 1024px) {
    margin-bottom: 28px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
  margin-bottom: 15px;

  @media (min-width: 1024px) {
    gap: 20px;
    margin-bottom: 25px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  width: 100%;
`;

const Label = styled.label`
  font-size: ${theme.typography.small};
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: -0.01em;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1.5px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  font-size: ${theme.typography.body};
  transition: all ${theme.transitions.base};
  background: ${theme.colors.card};
  box-sizing: border-box;
  height: 44px;
  color: ${theme.colors.text};

  @media (min-width: 1024px) {
    height: 48px;
    padding: 10px 16px 10px 40px;
  }

  &:focus {
    border-color: ${theme.colors.primary};
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.12);
  }

  &.error {
    border-color: ${theme.colors.error};
    box-shadow: 0 0 0 4px ${theme.colors.errorBg};
  }
`;

const InputIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textMuted};
  font-size: 16px;
`;

const ErrorText = styled.span`
  color: ${theme.colors.error};
  font-size: ${theme.typography.small};
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SubmitButton = styled.button`
  background: ${theme.colors.accentGradient};
  color: white;
  padding: 10px;
  border-radius: ${theme.radius.md};
  font-weight: 700;
  font-size: ${theme.typography.body};
  border: none;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  width: 100%;
  margin-top: 10px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.glowSoft};
  letter-spacing: -0.01em;

  @media (min-width: 1024px) {
    height: 48px;
    margin-top: 14px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.glow};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

const RegisterLink = styled(Link)`
  color: ${theme.colors.primary};
  text-align: center;
  display: block;
  margin-top: 15px;
  text-decoration: none;
  font-weight: 600;
  transition: all ${theme.transitions.fast};
  padding: 8px;
  border-radius: ${theme.radius.sm};
  font-size: ${theme.typography.body};

  &:hover {
    color: ${theme.colors.secondary};
    background: rgba(255, 107, 107, 0.08);
  }
`;

const ForgotPasswordLink = styled(Link)`
  color: ${theme.colors.textMuted};
  text-align: right;
  display: block;
  margin-top: 6px;
  text-decoration: none;
  font-size: ${theme.typography.small};
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primary};
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin: 15px 0;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.small};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${theme.colors.border};
  }
`;

const GoogleSection = styled.div`
  margin: 0 0 15px 0;
`;

const RequiredAsterisk = styled.span`
  color: ${theme.colors.error};
  margin-left: -5px;
`;

interface LoginForm {
  email: string;
  password: string;
}

interface ValidationErrors {
  email?: string;
  password?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [loading, setLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.email) {
      newErrors.email = 'Oops! You forgot to add your email.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Oops! You forgot to enter your password.';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Too short — your password must be 6 characters minimum.';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.password)) {
      newErrors.password = 'Your password needs to be stronger: add a capital letter, a lowercase letter, a number, and a special symbol.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const response = await loginUser(formData);
      console.log("🚀 ~ handleSubmit ~ response:", response)

      if (response && response.user) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        toast.success('Login successful!');
        navigate(APP_ROUTES.DASHBOARD);
      } else {
        toast.error('Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error((error as any)?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      console.log("Google credential received:", credentialResponse);
      const response = await googleAuth(credentialResponse.credential);
      console.log("Google auth response:", response);

      if (!response) {
        console.error("No response received from googleAuth");
        toast.error('Login failed: No response from server');
        return;
      }

      if (!response.data) {
        console.error("No data in response:", response);
        toast.error('Login failed: Invalid response format');
        return;
      }

      const user = response.data.user;
      const token = user.token;


      if (!token) {
        console.error("No token in response data:", response.data);
        toast.error('Login failed: No token received');
        return;
      }

      if (!user) {
        console.error("No user in response data:", response.data);
        toast.error('Login failed: No user data received');
        return;
      }

      // Store the token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      console.log("Token and user data stored successfully");
      console.log("Attempting to navigate to:", APP_ROUTES.DASHBOARD);

      toast.success('Google login successful!');

      // Force a hard navigation to dashboard
      window.location.href = APP_ROUTES.DASHBOARD;

    } catch (error) {
      console.error('Google login error:', error);
      toast.error(`Google login failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <LoginContainer>
      <HomeLink to="/">
        <FontAwesomeIcon icon={faHome} /> Back to Home
      </HomeLink>
      <LoginCard>
        <Title>Welcome Back</Title>
        <Subtitle>Sign in to continue to your account</Subtitle>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              <FontAwesomeIcon icon={faEnvelope} /> Email<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email"
              />
            </InputWrapper>
            {errors.email && <ErrorText>{errors.email}</ErrorText>}
          </FormGroup>

          <FormGroup>
            <Label>
              <FontAwesomeIcon icon={faLock} /> Password<RequiredAsterisk>*</RequiredAsterisk>
            </Label>
            <InputWrapper>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error' : ''}
                placeholder="Enter your password"
              />
            </InputWrapper>
            {errors.password && <ErrorText>{errors.password}</ErrorText>}
            <ForgotPasswordLink to="/forgot-password">
              Forgot your password?
            </ForgotPasswordLink>
          </FormGroup>

          <SubmitButton type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </SubmitButton>
        </Form>

        <Divider>or continue with</Divider>

        <GoogleSection>
          <GoogleAuthButton onSuccess={handleGoogleSuccess} />
        </GoogleSection>

        <RegisterLink to="/register">
          Don't have an account? Create one now
        </RegisterLink>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login; 